import { useLayoutEffect } from "react";

/** Matches the mobile layout breakpoint in `index.css`. */
export const COLLECT_PIN_MAX_WIDTH = 500;

/** One glide when the keyboard closes. Matches `vv-keyboard-close` in CSS. */
const KEYBOARD_CLOSE_MS = 560;

export function collectViewportPin(
  width: number,
  offsetTop: number,
  height: number
): { pinned: boolean; offsetTop: string; height: string } {
  if (width > COLLECT_PIN_MAX_WIDTH) {
    return { pinned: false, offsetTop: "0px", height: "100%" };
  }
  return {
    pinned: true,
    // Floor the top and ceil the height so a fractional viewport cannot
    // leave a hairline of the page (and its shadow) under the button.
    offsetTop: `${Math.max(0, Math.floor(offsetTop))}px`,
    height: `${Math.max(0, Math.ceil(height))}px`,
  };
}

/**
 * True when the keyboard gap is mostly gone. A 1px ceil bump, or the
 * accessory bar, must not play the close glide while the keyboard stays open.
 */
export function keyboardIsClosing(
  pinnedHeight: number,
  nextHeight: number,
  tallestHeight: number
): boolean {
  if (!(pinnedHeight > 0) || !(nextHeight > pinnedHeight)) return false;
  const wasOpen = tallestHeight - pinnedHeight > 150;
  const mostlyClosed = tallestHeight - nextHeight <= 150;
  return wasOpen && mostlyClosed;
}

/**
 * How far to move the form scroller so the focused field stays visible.
 * Positive scrolls down, negative scrolls up. Zero when the field is already
 * inside the box: spare space must not pull the list back to the top.
 */
export function collectFieldScrollDelta(
  fieldTop: number,
  fieldBottom: number,
  boxTop: number,
  boxBottom: number,
  padding = 16
): number {
  const below = fieldBottom - (boxBottom - padding);
  if (below > 1) return below;
  const above = boxTop + padding - fieldTop;
  if (above > 1) return -above;
  return 0;
}

function collectScroller(start: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = start.parentElement;
  while (node && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

/** Keep the focused field inside the scrolling form, not under the footer. */
export function revealFocusedCollectField(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  if (active.tagName !== "INPUT" && active.tagName !== "TEXTAREA" && active.tagName !== "SELECT") {
    return;
  }

  const scroller = collectScroller(active);
  if (!scroller) return;

  const field = active.closest(".mock-card-field");
  const target = field instanceof HTMLElement ? field : active;
  const box = scroller.getBoundingClientRect();
  const fieldRect = target.getBoundingClientRect();
  const delta = collectFieldScrollDelta(
    fieldRect.top,
    fieldRect.bottom,
    box.top,
    box.bottom
  );
  if (delta !== 0) {
    scroller.scrollTop += delta;
  }
}

/**
 * iOS shrinks `visualViewport` and shifts it with `offsetTop` when the
 * keyboard opens. Pin the collect screen to that visible region in the same
 * frame the browser reports it. Opening the keyboard and switching fields
 * stay instant. Closing it eases the sheet once, to the full height, so the
 * button is not pushed by every intermediate viewport event. The form scrolls
 * only when the focused field is clipped. While a finger is down, leave the
 * pin alone so the sheet does not shake.
 */
export function usePinCollectLayout(): void {
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    if (!viewport) return;

    let touching = false;
    let closing = false;
    let pinnedHeight = -1;
    let tallestHeight = 0;
    let closeTimer = 0;
    let closeFrame = 0;
    const closeMs = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : KEYBOARD_CLOSE_MS;

    const finishClose = () => {
      closing = false;
      root.classList.remove("vv-keyboard-close");
      if (!touching) {
        apply();
        revealFocusedCollectField();
      }
    };

    const apply = (): boolean => {
      const pin = collectViewportPin(
        window.innerWidth,
        viewport.offsetTop,
        viewport.height
      );
      if (!pin.pinned) {
        root.classList.remove("vv-pinned");
        root.classList.remove("vv-keyboard-close");
        root.style.removeProperty("--vv-offset-top");
        root.style.removeProperty("--vv-height");
        pinnedHeight = -1;
        tallestHeight = 0;
        closing = false;
        window.clearTimeout(closeTimer);
        window.cancelAnimationFrame(closeFrame);
        return false;
      }
      const nextHeight = Number.parseFloat(pin.height);
      if (nextHeight > tallestHeight) tallestHeight = nextHeight;
      if (closing) return false;

      const heightChanged = pinnedHeight !== nextHeight;
      const keyboardClosing = keyboardIsClosing(
        pinnedHeight,
        nextHeight,
        tallestHeight
      );
      root.classList.add("vv-pinned");
      if (keyboardClosing) {
        closing = true;
        const targetHeight = Math.max(tallestHeight, nextHeight);
        pinnedHeight = targetHeight;
        root.classList.add("vv-keyboard-close");
        window.cancelAnimationFrame(closeFrame);
        window.clearTimeout(closeTimer);
        closeFrame = window.requestAnimationFrame(() => {
          root.style.setProperty("--vv-offset-top", pin.offsetTop);
          root.style.setProperty("--vv-height", `${targetHeight}px`);
          closeTimer = window.setTimeout(finishClose, closeMs);
        });
        return false;
      }
      pinnedHeight = nextHeight;
      root.style.setProperty("--vv-offset-top", pin.offsetTop);
      root.style.setProperty("--vv-height", pin.height);
      return heightChanged;
    };

    const onResize = () => {
      if (touching) return;
      if (apply()) revealFocusedCollectField();
    };

    const onViewportScroll = () => {
      if (touching) return;
      if (apply()) revealFocusedCollectField();
    };

    const onFocusIn = () => {
      revealFocusedCollectField();
    };

    const onTouchStart = () => {
      touching = true;
    };

    const onTouchEnd = () => {
      touching = false;
      if (!closing) {
        apply();
        revealFocusedCollectField();
      }
    };

    apply();
    viewport.addEventListener("resize", onResize);
    viewport.addEventListener("scroll", onViewportScroll);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.clearTimeout(closeTimer);
      window.cancelAnimationFrame(closeFrame);
      viewport.removeEventListener("resize", onResize);
      viewport.removeEventListener("scroll", onViewportScroll);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      root.classList.remove("vv-pinned");
      root.classList.remove("vv-keyboard-close");
      root.style.removeProperty("--vv-offset-top");
      root.style.removeProperty("--vv-height");
    };
  }, []);
}
